import { createContext, useContext, useState, useEffect } from 'react';
import { translations } from './translations';
import { applyDocumentLanguage } from './documentLanguage';

const LanguageContext = createContext();

// Supported languages with their details
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', dir: 'ltr' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', dir: 'ltr' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', dir: 'ltr' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', dir: 'ltr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', dir: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },
];

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexus-lang');
      if (saved && SUPPORTED_LANGUAGES.find(l => l.code === saved)) {
        return saved;
      }
      // Try to detect browser language
      const browserLang = navigator.language?.split('-')[0];
      if (browserLang && SUPPORTED_LANGUAGES.find(l => l.code === browserLang)) {
        return browserLang;
      }
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('nexus-lang', language);
    applyDocumentLanguage(document.documentElement, language, SUPPORTED_LANGUAGES);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    // Try current language first
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    // Fallback to English if not found
    if (value === undefined) {
      value = translations['en'];
      for (const k of keys) {
        value = value?.[k];
      }
    }
    return value || key;
  };

  // Cycle through languages
  const toggleLanguage = () => {
    const currentIndex = SUPPORTED_LANGUAGES.findIndex(l => l.code === language);
    const nextIndex = (currentIndex + 1) % SUPPORTED_LANGUAGES.length;
    setLanguage(SUPPORTED_LANGUAGES[nextIndex].code);
  };

  // Get current language config
  const getCurrentLanguage = () => {
    return SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      toggleLanguage, 
      t, 
      getCurrentLanguage,
      supportedLanguages: SUPPORTED_LANGUAGES 
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
